import {
  getCancellationAccess,
  actionResponseSchema,
  adminOptionsResponseSchema,
  adminUserInputSchema,
  adminUserMutationResponseSchema,
  adminUsersResponseSchema,
  catalogPriceUpdateSchema,
  catalogProductCreateSchema,
  catalogProductResolutionResponseSchema,
  catalogProductResponseSchema,
  catalogProductsResponseSchema,
  healthResponseSchema,
  inventoryCategoriesResponseSchema,
  lensPriceCatalogResponseSchema,
  lensPriceCombinationInputSchema,
  lensPriceCombinationResponseSchema,
  lensPriceCombinationsResponseSchema,
  lensPriceComponentInputSchema,
  lensPriceComponentResponseSchema,
  lensPriceComponentUpdateSchema,
  loginResponseSchema,
  sessionResponseSchema,
  type AdminOptionsResponse,
  type AdminUserInput,
  type AdminUserRecord,
  type CatalogProduct,
  type HealthResponse,
  type InventoryCategory,
  type LensDesign,
  type LensMaterial,
  type LensPriceCombination,
  type LensPriceComponent,
  type LensPriceComponentType,
  type LensPriceOptions,
  type LensType,
  type RoleCode,
  type SessionIdentity,
} from "@cosmo/contracts";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { CounterSalesOperations } from "./CounterSalesOperations";
import { BillingOperations } from "./BillingOperations";
import { CancellationOperations } from "./CancellationOperations";
import { CashDrawerAdministration } from "./CashDrawerAdministration";
import { ClinicalRecordsOperations } from "./ClinicalRecordsOperations";
import { InventoryOperations } from "./InventoryOperations";
import { KpiOperations } from "./KpiOperations";
import { NotificationCenter } from "./NotificationCenter";
import { SalesOperations } from "./SalesOperations";
import { WorkControlOperations } from "./WorkControlOperations";

type ApiState =
  | { kind: "loading" }
  | { kind: "ready"; health: HealthResponse }
  | { kind: "offline" };

type AuthState =
  | { kind: "checking" }
  | { kind: "anonymous" }
  | { kind: "authenticated"; session: SessionIdentity };

type UserForm = {
  username: string;
  displayName: string;
  employeeNumber: string;
  email: string;
  status: "ACTIVE" | "INACTIVE";
  roleCodes: RoleCode[];
  branchIds: string[];
};

type AdminOptions = AdminOptionsResponse["data"];

type CatalogForm = {
  partNumber: string;
  categoryCode: InventoryCategory["code"];
  brand: string;
  material: string;
  color: string;
  measureA: string;
  measureB: string;
  measureDbl: string;
  measureEd: string;
  baseCurve: string;
  acquisitionCost: string;
  multiplier: string;
  manualPublicPrice: string;
  status: "ACTIVE" | "INACTIVE";
};

type LensPriceDraft = {
  componentType: LensPriceComponentType;
  lensType: LensType;
  design: LensDesign;
  material: LensMaterial;
  optionName: string;
  acquisitionCost: string;
  salePrice: string;
};

type LensCombinationDraft = {
  basePriceId: string;
  arPriceId: string;
  photoPriceId: string;
  photoColor: string;
  uvPriceId: string;
  appliesRangeRx: boolean;
  rangePriceId: string;
  preferredSupplier: string;
  alternateSuppliers: string;
};

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

async function loadHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const options: RequestInit = {
    credentials: "include",
    headers: { Accept: "application/json" },
  };
  if (signal) options.signal = signal;
  const response = await fetch(`${apiBaseUrl}/api/v1/health`, options);
  if (!response.ok) throw new Error("API unavailable");
  return healthResponseSchema.parse(await response.json());
}

async function loadSession(signal?: AbortSignal): Promise<SessionIdentity | null> {
  const options: RequestInit = {
    credentials: "include",
    headers: { Accept: "application/json" },
  };
  if (signal) options.signal = signal;
  const response = await fetch(`${apiBaseUrl}/api/v1/session`, options);
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("Session unavailable");
  return sessionResponseSchema.parse(await response.json()).data;
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message || "No fue posible completar la solicitud.";
  } catch {
    return "No fue posible completar la solicitud.";
  }
}

function BrandHeader({ session }: { session: SessionIdentity | undefined }) {
  return (
    <header className="topbar">
      {session?.branchId && <NotificationCenter
        onOpenCancellations={() => window.dispatchEvent(new CustomEvent("cosmo:open-cancellations"))}
        session={session}
      />}
      <img className="brand-logo" src="/logo-cosmo-opticas.jpg" alt="Cosmo Ópticas" />
      <div className="topbar-actions">
        {session && <span className="user-name">{session.displayName}</span>}
        <span className="phase-badge">ENTORNO DE PRUEBAS</span>
      </div>
    </header>
  );
}

type ModuleIconName = "graduated" | "counter" | "inventory" | "work" | "clinical" | "billing" | "kpi" | "admin" | "cancellation";

function ModuleIcon({ name }: { name: ModuleIconName }) {
  const common = { fill: "none", stroke: "currentColor", strokeLinecap: "round" as const, strokeLinejoin: "round" as const, strokeWidth: 1.8 };
  const drawings = {
    graduated: <><path {...common} d="M8 4h8l1 3h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2l1-3Z" /><path {...common} d="M12 9v7M8.5 12.5h7" /></>,
    counter: <><rect {...common} height="15" rx="2" width="18" x="3" y="5" /><path {...common} d="M3 10h18M7 16h4" /></>,
    inventory: <><path {...common} d="m12 3 8 4-8 4-8-4 8-4Z" /><path {...common} d="m4 12 8 4 8-4M4 17l8 4 8-4M4 7v10M20 7v10M12 11v10" /></>,
    work: <><path {...common} d="M9 4h6l1 2h3v14H5V6h3l1-2Z" /><circle {...common} cx="14.5" cy="14.5" r="3.5" /><path {...common} d="M14.5 12.5v2.2l1.4.8" /></>,
    clinical: <><path {...common} d="M3 7h7l2 2h9v10H3V7Z" /><path {...common} d="M12 11v6M9 14h6" /></>,
    billing: <><path {...common} d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21V3Z" /><path {...common} d="M9 7h6M9 11h6M9 15h3" /></>,
    kpi: <><path {...common} d="M4 20V10M10 20V4M16 20v-7M22 20H2" /><path {...common} d="m3 7 6-3 6 5 6-6" /></>,
    admin: <><circle {...common} cx="9" cy="8" r="3" /><path {...common} d="M3.5 19a5.5 5.5 0 0 1 11 0" /><circle {...common} cx="18" cy="15" r="2.5" /><path {...common} d="M18 10.5v2M18 17.5v2M13.5 15h2M20.5 15h2" /></>,
    cancellation: <><path {...common} d="M6 3h9l4 4v14H6V3Z" /><path {...common} d="M15 3v5h4M9 12l6 6M15 12l-6 6" /></>,
  } satisfies Record<ModuleIconName, ReactNode>;
  return <span className="card-icon" aria-hidden="true"><svg viewBox="0 0 24 24">{drawings[name]}</svg></span>;
}

function ModuleCard({ action, description, enabled, icon, onOpen, title }: {
  action: string;
  description: string;
  enabled: boolean;
  icon: ModuleIconName;
  onOpen: () => void;
  title: string;
}) {
  return <article className={`module-card${enabled ? "" : " disabled-card"}`}>
    <ModuleIcon name={icon} />
    <h2>{title}</h2>
    <p>{description}</p>
    {enabled && <button className="card-button" onClick={onOpen}>{action}</button>}
  </article>;
}

function LoginPanel({ enabled, onAuthenticated }: { enabled: boolean; onAuthenticated: (session: SessionIdentity) => void }) {
  const [tenantSlug, setTenantSlug] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ tenantSlug, username, password }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      onAuthenticated(loginResponseSchema.parse(await response.json()).data);
      setPassword("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible iniciar la sesión.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="access-layout" aria-labelledby="access-title">
      <form className="access-card" onSubmit={submit}>
        <p className="eyebrow">Acceso protegido</p>
        <h1 id="access-title">Ingresa a tu espacio de trabajo</h1>
        <p className="form-intro">Usa el código asignado a tu empresa y tus datos personales de acceso.</p>
        <label>Código de empresa<input autoComplete="organization" disabled={!enabled || submitting} onChange={(event) => setTenantSlug(event.target.value.toLowerCase())} placeholder="ej. cosmo-pruebas" required value={tenantSlug} /></label>
        <label>Usuario<input autoComplete="username" disabled={!enabled || submitting} onChange={(event) => setUsername(event.target.value)} required value={username} /></label>
        <label>Contraseña<input autoComplete="current-password" disabled={!enabled || submitting} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} /></label>
        {error && <p className="form-message error-message">{error}</p>}
        <button className="primary-button" disabled={!enabled || submitting}>{submitting ? "Verificando acceso…" : "Ingresar"}</button>
        <small>El acceso se bloquea temporalmente después de varios intentos incorrectos.</small>
      </form>
      <div className="access-summary">
        <p className="eyebrow">Gestión sencilla y centralizada</p>
        <h2>Todo lo que necesita tu óptica, en un solo lugar.</h2>
        <p>Ventas, inventario, expedientes, administración y resultados con acceso controlado por empresa y sucursal.</p>
        <div className="security-note">Esta versión utiliza exclusivamente información ficticia para validación.</div>
      </div>
    </section>
  );
}

function PasswordChangePanel({ session, onCompleted, onLogout }: { session: SessionIdentity; onCompleted: (session: SessionIdentity) => void; onLogout: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setMessage("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/change-password`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      actionResponseSchema.parse(await response.json());
      const refreshed = await loadSession();
      if (!refreshed) throw new Error("La sesión dejó de estar disponible.");
      onCompleted(refreshed);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible cambiar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  }

  async function logout() {
    setSubmitting(true);
    try {
      await fetch(`${apiBaseUrl}/api/v1/auth/logout`, { method: "POST", credentials: "include", headers: { Accept: "application/json" } });
    } finally {
      onLogout();
      setSubmitting(false);
    }
  }

  return (
    <section className="single-panel">
      <form className="access-card" onSubmit={submit}>
        <p className="eyebrow">Primer ingreso</p>
        <h1>Crea tu contraseña personal</h1>
        <p className="form-intro">{session.displayName}, reemplaza la contraseña temporal antes de continuar.</p>
        <label>Contraseña temporal<input autoComplete="current-password" disabled={submitting} onChange={(event) => setCurrentPassword(event.target.value)} required type="password" value={currentPassword} /></label>
        <label>Nueva contraseña<input autoComplete="new-password" disabled={submitting} onChange={(event) => setNewPassword(event.target.value)} required type="password" value={newPassword} /></label>
        <label>Confirmar nueva contraseña<input autoComplete="new-password" disabled={submitting} onChange={(event) => setConfirmation(event.target.value)} required type="password" value={confirmation} /></label>
        <p className="password-rule">Mínimo 10 caracteres con mayúscula, minúscula, número y símbolo.</p>
        {message && <p className="form-message error-message">{message}</p>}
        <div className="form-actions"><button className="primary-button" disabled={submitting}>{submitting ? "Procesando…" : "Guardar contraseña"}</button><button className="secondary-button" disabled={submitting} onClick={logout} type="button">Cerrar sesión</button></div>
      </form>
    </section>
  );
}

function BranchSelectionPanel({ session, onSelected, onLogout }: { session: SessionIdentity; onSelected: (session: SessionIdentity) => void; onLogout: () => void }) {
  const [branchId, setBranchId] = useState(session.branches[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  async function selectBranch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/select-branch`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ branchId }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      onSelected(sessionResponseSchema.parse(await response.json()).data);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible seleccionar la sucursal.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="single-panel">
      <form className="access-card" onSubmit={selectBranch}>
        <p className="eyebrow">Sucursal de trabajo</p>
        <h1>Selecciona dónde trabajarás</h1>
        <p className="form-intro">La información y las operaciones quedarán ligadas a esta sucursal.</p>
        {session.branches.length > 0 ? <label>Sucursal<select disabled={submitting} onChange={(event) => setBranchId(event.target.value)} required value={branchId}>{session.branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.code} · {branch.name}</option>)}</select></label> : <p className="form-message error-message">Tu cuenta todavía no tiene una sucursal asignada.</p>}
        {message && <p className="form-message error-message">{message}</p>}
        <div className="form-actions"><button className="primary-button" disabled={submitting || !branchId}>{submitting ? "Ingresando…" : "Entrar a la sucursal"}</button><button className="secondary-button" disabled={submitting} onClick={onLogout} type="button">Cerrar sesión</button></div>
      </form>
    </section>
  );
}

function blankUserForm(options: AdminOptions): UserForm {
  return { username: "", displayName: "", employeeNumber: "", email: "", status: "ACTIVE", roleCodes: options.roles[0] ? [options.roles[0].code] : [], branchIds: options.branches[0] ? [options.branches[0].id] : [] };
}

function userFormFromRecord(user: AdminUserRecord): UserForm {
  return { username: user.username, displayName: user.displayName, employeeNumber: user.employeeNumber || "", email: user.email || "", status: user.status === "ACTIVE" ? "ACTIVE" : "INACTIVE", roleCodes: user.roleCodes, branchIds: user.branchIds };
}

function formatDate(value: string | null): string {
  if (!value) return "Sin acceso registrado";
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function UserAdministration({ onCashDrawer, onClose }: { session: SessionIdentity; onCashDrawer: () => void; onClose: () => void }) {
  const [options, setOptions] = useState<AdminOptions | null>(null);
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [form, setForm] = useState<UserForm | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [temporaryAccess, setTemporaryAccess] = useState<{ username: string; password: string } | null>(null);

  async function reloadUsers() {
    const response = await fetch(`${apiBaseUrl}/api/v1/admin/users`, { credentials: "include", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(await responseMessage(response));
    setUsers(adminUsersResponseSchema.parse(await response.json()).data);
  }

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${apiBaseUrl}/api/v1/admin/options`, { credentials: "include", headers: { Accept: "application/json" } }),
      fetch(`${apiBaseUrl}/api/v1/admin/users`, { credentials: "include", headers: { Accept: "application/json" } }),
    ])
      .then(async ([optionsResponse, usersResponse]) => {
        if (!optionsResponse.ok) throw new Error(await responseMessage(optionsResponse));
        if (!usersResponse.ok) throw new Error(await responseMessage(usersResponse));
        const nextOptions = adminOptionsResponseSchema.parse(await optionsResponse.json()).data;
        const nextUsers = adminUsersResponseSchema.parse(await usersResponse.json()).data;
        if (!active) return;
        setOptions(nextOptions);
        setUsers(nextUsers);
        setForm(blankUserForm(nextOptions));
      })
      .catch((cause: unknown) => { if (active) setMessage(cause instanceof Error ? cause.message : "No fue posible cargar los usuarios."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es-MX");
    if (!term) return users;
    return users.filter((user) => [user.displayName, user.username, user.employeeNumber || ""].some((value) => value.toLocaleLowerCase("es-MX").includes(term)));
  }, [search, users]);

  function toggleRole(role: RoleCode) {
    if (!form) return;
    setForm({ ...form, roleCodes: form.roleCodes.includes(role) ? form.roleCodes.filter((item) => item !== role) : [...form.roleCodes, role] });
  }

  function toggleBranch(branchId: string) {
    if (!form) return;
    setForm({ ...form, branchIds: form.branchIds.includes(branchId) ? form.branchIds.filter((item) => item !== branchId) : [...form.branchIds, branchId] });
  }

  function startNew() {
    if (!options) return;
    setEditingId(null);
    setForm(blankUserForm(options));
    setTemporaryAccess(null);
    setMessage("");
  }

  function startEdit(user: AdminUserRecord) {
    setEditingId(user.id);
    setForm(userFormFromRecord(user));
    setTemporaryAccess(null);
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || !options) return;
    const payload: AdminUserInput = { username: form.username.trim().toLowerCase(), displayName: form.displayName.trim(), employeeNumber: form.employeeNumber.trim() || null, email: form.email.trim().toLowerCase() || null, status: form.status, roleCodes: form.roleCodes, branchIds: form.branchIds };
    const validated = adminUserInputSchema.safeParse(payload);
    if (!validated.success) {
      setMessage("Completa los datos y selecciona al menos un perfil y una sucursal.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/admin/users${editingId ? `/${editingId}` : ""}`, {
        method: editingId ? "PATCH" : "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(validated.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = adminUserMutationResponseSchema.parse(await response.json()).data;
      await reloadUsers();
      if (result.temporaryPassword) {
        setTemporaryAccess({ username: result.user.username, password: result.temporaryPassword });
        setMessage("Usuario creado. Entrega los datos temporales directamente al empleado.");
      } else {
        setMessage("Los cambios quedaron guardados y las sesiones anteriores fueron cerradas.");
      }
      setEditingId(null);
      setForm(blankUserForm(options));
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible guardar el usuario.");
    } finally {
      setSaving(false);
    }
  }

  async function resetPassword(user: AdminUserRecord) {
    if (!window.confirm(`¿Restaurar el acceso de ${user.displayName}? Sus sesiones abiertas se cerrarán.`)) return;
    setSaving(true);
    setMessage("");
    setTemporaryAccess(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/admin/users/${user.id}/reset-password`, { method: "POST", credentials: "include", headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = adminUserMutationResponseSchema.parse(await response.json()).data;
      await reloadUsers();
      if (!result.temporaryPassword) throw new Error("No se recibió la contraseña temporal.");
      setTemporaryAccess({ username: result.user.username, password: result.temporaryPassword });
      setMessage("Acceso restaurado. La contraseña temporal se muestra una sola vez.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible restaurar el acceso.");
    } finally {
      setSaving(false);
    }
  }

  async function copyTemporaryAccess() {
    if (!temporaryAccess) return;
    await navigator.clipboard.writeText(`Usuario: ${temporaryAccess.username}\nContraseña temporal: ${temporaryAccess.password}`);
    setMessage("Datos temporales copiados.");
  }

  if (loading || !options || !form) return <section className="loading-panel"><span className="loading-ring" aria-hidden="true" /><h1>Cargando usuarios</h1><p>Preparando perfiles y sucursales…</p></section>;

  return (
    <section className="admin-shell" aria-labelledby="user-admin-title">
      <div className="section-heading"><div><p className="eyebrow">Control administrativo</p><h1 id="user-admin-title">Usuarios y accesos</h1><p>Administra perfiles y sucursales sin borrar el historial.</p></div><div className="step-actions heading-actions"><button className="secondary-button" onClick={onCashDrawer}>Caja e impresora</button><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div></div>
      {temporaryAccess && <aside className="temporary-access" aria-live="polite"><div><strong>Datos temporales</strong><span>Se muestran únicamente para entregarlos al empleado.</span></div><code>{temporaryAccess.username}</code><code>{temporaryAccess.password}</code><button className="secondary-button compact-button" onClick={copyTemporaryAccess}>Copiar datos</button></aside>}
      {message && <p className="form-message info-message">{message}</p>}
      <div className="admin-grid">
        <form className="user-editor" onSubmit={save}>
          <div className="editor-title"><div><h2>{editingId ? "Editar usuario" : "Nuevo usuario"}</h2><p>{editingId ? "Actualiza los datos autorizados." : "La contraseña temporal se generará automáticamente."}</p></div>{editingId && <button className="text-button" onClick={startNew} type="button">Cancelar edición</button>}</div>
          <div className="field-grid">
            <label>Usuario<input autoComplete="off" disabled={saving} onChange={(event) => setForm({ ...form, username: event.target.value.toLowerCase() })} placeholder="ej. ana.lopez" required value={form.username} /></label>
            <label>Nombre completo<input disabled={saving} onChange={(event) => setForm({ ...form, displayName: event.target.value })} required value={form.displayName} /></label>
            <label>Número de empleado<input disabled={saving} onChange={(event) => setForm({ ...form, employeeNumber: event.target.value })} value={form.employeeNumber} /></label>
            <label>Correo electrónico<input disabled={saving} onChange={(event) => setForm({ ...form, email: event.target.value })} type="email" value={form.email} /></label>
            <label>Estado<select disabled={saving} onChange={(event) => setForm({ ...form, status: event.target.value as "ACTIVE" | "INACTIVE" })} value={form.status}><option value="ACTIVE">Activo</option><option value="INACTIVE">Inactivo</option></select></label>
          </div>
          <fieldset><legend>Perfiles permitidos</legend><div className="choice-grid">{options.roles.map((role) => <label className="choice-card" key={role.code}><input checked={form.roleCodes.includes(role.code)} disabled={saving} onChange={() => toggleRole(role.code)} type="checkbox" /><span><strong>{role.name}</strong><small>{role.description}</small></span></label>)}</div></fieldset>
          <fieldset><legend>Sucursales permitidas</legend><div className="choice-grid compact-choices">{options.branches.map((branch) => <label className="choice-card" key={branch.id}><input checked={form.branchIds.includes(branch.id)} disabled={saving} onChange={() => toggleBranch(branch.id)} type="checkbox" /><span><strong>{branch.name}</strong><small>{branch.code}</small></span></label>)}</div></fieldset>
          <button className="primary-button" disabled={saving}>{saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear usuario"}</button>
        </form>
        <div className="user-directory">
          <div className="directory-toolbar"><div><h2>Usuarios registrados</h2><p>{users.length} cuenta{users.length === 1 ? "" : "s"} en la empresa.</p></div><input aria-label="Buscar usuario" onChange={(event) => setSearch(event.target.value)} placeholder="Buscar nombre o usuario" type="search" value={search} /></div>
          <div className="user-list">
            {filteredUsers.map((user) => {
              const locked = Boolean(user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now());
              return <article className="user-row" key={user.id}><div className="user-main"><div className="avatar" aria-hidden="true">{user.displayName.slice(0, 2).toUpperCase()}</div><div><h3>{user.displayName}{user.isCurrentUser && <span className="self-label">Tu cuenta</span>}</h3><p>{user.username}{user.employeeNumber ? ` · Empleado ${user.employeeNumber}` : ""}</p><div className="tag-list">{user.roleCodes.map((role) => <span key={role}>{options.roles.find((item) => item.code === role)?.name || role}</span>)}</div></div></div><div className="user-meta"><span className={`status-pill ${user.status === "ACTIVE" && !locked ? "active-status" : "inactive-status"}`}>{locked ? "Bloqueado temporalmente" : user.status === "ACTIVE" ? "Activo" : "Inactivo"}</span><small>Último acceso: {formatDate(user.lastLoginAt)}</small><small>{user.branchIds.length} sucursal{user.branchIds.length === 1 ? "" : "es"} asignada{user.branchIds.length === 1 ? "" : "s"}</small></div><div className="row-actions">{user.isCurrentUser ? <span className="protected-note">Cuenta protegida</span> : <><button className="secondary-button compact-button" disabled={saving} onClick={() => startEdit(user)}>Editar</button><button className="text-button" disabled={saving} onClick={() => resetPassword(user)}>Restaurar contraseña</button></>}</div></article>;
            })}
            {filteredUsers.length === 0 && <p className="empty-state">No se encontraron usuarios con ese criterio.</p>}
          </div>
        </div>
      </div>
    </section>
  );
}

function blankCatalogForm(categories: InventoryCategory[]): CatalogForm {
  return {
    partNumber: "", categoryCode: categories[0]?.code || "Sol G", brand: "",
    material: "", color: "", measureA: "", measureB: "", measureDbl: "",
    measureEd: "", baseCurve: "", acquisitionCost: "", multiplier: "2.5",
    manualPublicPrice: "", status: "ACTIVE",
  };
}

function catalogFormFromProduct(product: CatalogProduct): CatalogForm {
  return {
    partNumber: product.partNumber, categoryCode: product.categoryCode,
    brand: product.brand, material: product.material, color: product.color,
    measureA: product.measureA?.toString() || "", measureB: product.measureB?.toString() || "",
    measureDbl: product.measureDbl?.toString() || "", measureEd: product.measureEd?.toString() || "",
    baseCurve: product.baseCurve?.toString() || "", acquisitionCost: product.acquisitionCost.toString(),
    multiplier: product.multiplier.toString(), manualPublicPrice: product.manualPublicPrice?.toString() || "",
    status: product.status,
  };
}

function money(value: number | null): string {
  if (value === null) return "Pendiente";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);
}

function sameMoney(left: number | null, right: number | null): boolean {
  if (left === null || right === null) return left === right;
  return Math.round(left * 100) === Math.round(right * 100);
}

function previewPublicPrice(form: CatalogForm, currentPrice = 0): number {
  const round = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const lower = Math.floor(value / 50) * 50;
    return value - lower < 25 ? lower : lower + 50;
  };
  return Math.max(
    currentPrice,
    round(Number(form.acquisitionCost || 0) * Number(form.multiplier || 0)),
    round(Number(form.manualPublicPrice || 0)),
  );
}

const lensCardDetails: Array<{ type: LensPriceComponentType; title: string; description: string }> = [
  { type: "BASE", title: "Precio base", description: "Tipo, diseño y material del lente." },
  { type: "AR", title: "Antirreflejante", description: "Costo y precio por tratamiento." },
  { type: "FOTOSENSIBLE", title: "Fotosensible", description: "El color se elige al formar la combinación." },
  { type: "UV_COLOR", title: "Color o espejeado", description: "Precio independiente del color aplicado." },
  { type: "RANGO_RX", title: "Rango RX", description: "Cargo general para graduaciones fuera de rango." },
];

function blankLensPriceDraft(type: LensPriceComponentType, options: LensPriceOptions): LensPriceDraft {
  const lensType: LensType = options.lensTypes[0] || "Visión sencilla";
  return {
    componentType: type,
    lensType,
    design: options.designsByType[lensType][0] || "Convencional",
    material: options.materials[0] || "CR-39",
    optionName: type === "AR"
      ? options.antireflective[0] || ""
      : type === "FOTOSENSIBLE"
        ? options.photosensitive[0] || ""
        : type === "UV_COLOR"
          ? options.uvColor[0] || ""
          : "",
    acquisitionCost: "",
    salePrice: "",
  };
}

function blankLensCombination(): LensCombinationDraft {
  return {
    basePriceId: "", arPriceId: "", photoPriceId: "", photoColor: "",
    uvPriceId: "", appliesRangeRx: false, rangePriceId: "",
    preferredSupplier: "", alternateSuppliers: "",
  };
}

function LensPriceCatalog({
  session,
  onClose,
  onProducts,
  onInventory,
}: {
  session: SessionIdentity;
  onClose: () => void;
  onProducts: () => void;
  onInventory: () => void;
}) {
  const [options, setOptions] = useState<LensPriceOptions | null>(null);
  const [prices, setPrices] = useState<LensPriceComponent[]>([]);
  const [combinations, setCombinations] = useState<LensPriceCombination[]>([]);
  const [draft, setDraft] = useState<LensPriceDraft | null>(null);
  const [combination, setCombination] = useState<LensCombinationDraft>(blankLensCombination());
  const [editing, setEditing] = useState<LensPriceComponent | null>(null);
  const [editCost, setEditCost] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editStatus, setEditStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [editReason, setEditReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const canManage = session.roles.includes("ADMINISTRADOR")
    && session.permissions.includes("INVENTORY_WRITE")
    && session.permissions.includes("ADMIN_WRITE");
  const canImportProtectedTemplate = canManage
    && window.location.hostname === "pruebas.cosmoopticas.com.mx";

  async function loadLensCatalog() {
    const refreshToken = Date.now().toString();
    const freshRequest: RequestInit = {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    };
    const [pricesResponse, combinationsResponse] = await Promise.all([
      fetch(`${apiBaseUrl}/api/v1/catalog/lens-prices?refresh=${refreshToken}`, freshRequest),
      fetch(`${apiBaseUrl}/api/v1/catalog/lens-combinations?refresh=${refreshToken}`, freshRequest),
    ]);
    if (!pricesResponse.ok) throw new Error(await responseMessage(pricesResponse));
    if (!combinationsResponse.ok) throw new Error(await responseMessage(combinationsResponse));
    const catalog = lensPriceCatalogResponseSchema.parse(await pricesResponse.json()).data;
    const nextCombinations = lensPriceCombinationsResponseSchema.parse(await combinationsResponse.json()).data;
    setOptions(catalog.options);
    setPrices(catalog.prices);
    setCombinations(nextCombinations);
    setDraft((current) => current || blankLensPriceDraft("BASE", catalog.options));
    return { options: catalog.options, prices: catalog.prices, combinations: nextCombinations };
  }

  useEffect(() => {
    let active = true;
    loadLensCatalog()
      .catch((cause: unknown) => { if (active) setMessage(cause instanceof Error ? cause.message : "No fue posible cargar los precios de lentes."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  function selectCard(type: LensPriceComponentType) {
    if (!options) return;
    setEditing(null);
    setDraft(blankLensPriceDraft(type, options));
    setMessage("");
  }

  function startEditing(price: LensPriceComponent) {
    const isInitialCapture = price.salePrice === null;
    setEditing(price);
    setEditCost(price.acquisitionCost?.toString() || "");
    setEditPrice(price.salePrice?.toString() || "");
    setEditStatus(price.status);
    setEditReason("");
    setMessage(isInitialCapture
      ? `Captura los importes iniciales de ${price.label}. Al confirmar el precio de venta, la ficha se habilitará automáticamente en Venta oftálmica.`
      : `Modificarás ${price.label}. La identidad de la ficha no cambiará.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function persistExistingPrice(
    price: LensPriceComponent,
    input: { acquisitionCost: number | null; salePrice: number | null; status: "ACTIVE" | "INACTIVE"; reason: string },
  ) {
    const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-prices/${price.id}`, {
      method: "PATCH", credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    const saved = lensPriceComponentResponseSchema.parse(await response.json()).data;
    const refreshed = await loadLensCatalog();
    const verified = refreshed.prices.find((item) => item.id === saved.id);
    if (!verified
      || !sameMoney(verified.acquisitionCost, input.acquisitionCost)
      || !sameMoney(verified.salePrice, input.salePrice)
      || verified.status !== input.status) {
      throw new Error("El servidor no confirmó los importes guardados. La captura continúa abierta para que no se pierdan los datos.");
    }
    const affected = refreshed.combinations.filter((item) => [
      item.basePriceId, item.arPriceId, item.photoPriceId, item.uvPriceId, item.rangePriceId,
    ].includes(verified.id));
    return {
      verified,
      affectedCount: affected.length,
      readyCount: affected.filter((item) => item.readyForSale).length,
    };
  }

  async function savePrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft) return;
    const payload = {
      componentType: draft.componentType,
      lensType: draft.componentType === "BASE" ? draft.lensType : null,
      design: draft.componentType === "BASE" ? draft.design : null,
      material: draft.componentType === "BASE" ? draft.material : null,
      optionName: ["AR", "FOTOSENSIBLE", "UV_COLOR"].includes(draft.componentType) ? draft.optionName : null,
      acquisitionCost: draft.acquisitionCost.trim() ? Number(draft.acquisitionCost) : null,
      salePrice: draft.salePrice.trim() ? Number(draft.salePrice) : null,
    };
    const parsed = lensPriceComponentInputSchema.safeParse(payload);
    if (!parsed.success) { setMessage("Completa la ficha y revisa los importes antes de guardar."); return; }
    const existing = prices.find((price) => price.componentType === parsed.data.componentType
      && price.lensType === parsed.data.lensType
      && price.design === parsed.data.design
      && price.material === parsed.data.material
      && price.optionName === parsed.data.optionName);
    if (existing) {
      if (existing.salePrice === null) {
        if (parsed.data.salePrice === null) {
          setMessage("Captura el precio de venta para habilitar esta ficha en Venta oftálmica.");
          return;
        }
        setSaving(true); setMessage("");
        try {
          const result = await persistExistingPrice(existing, {
            acquisitionCost: parsed.data.acquisitionCost,
            salePrice: parsed.data.salePrice,
            status: "ACTIVE",
            reason: "Captura inicial de precios en ficha previamente cargada",
          });
          setEditing(null);
          setDraft(options ? blankLensPriceDraft(draft.componentType, options) : draft);
          setMessage(`Captura inicial guardada y verificada: ${result.verified.label} · ${money(result.verified.salePrice)}. ${result.affectedCount} combinaciones recalculadas; ${result.readyCount} listas para Venta oftálmica.`);
        } catch (cause) {
          setMessage(cause instanceof Error ? cause.message : "No fue posible guardar los precios iniciales.");
        } finally { setSaving(false); }
        return;
      }
      setEditing(existing);
      setEditCost(draft.acquisitionCost.trim() ? draft.acquisitionCost : existing.acquisitionCost?.toString() || "");
      setEditPrice(draft.salePrice.trim() ? draft.salePrice : existing.salePrice?.toString() || "");
      setEditStatus(existing.status);
      setEditReason("");
      setMessage(`La ficha ${existing.priceCode} ya tiene precios vigentes. Para reemplazarlos se requiere un motivo de auditoría.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-prices`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = lensPriceComponentResponseSchema.parse(await response.json()).data;
      await loadLensCatalog();
      setDraft(options ? blankLensPriceDraft(draft.componentType, options) : draft);
      setMessage(`Ficha ${saved.priceCode} guardada: ${saved.label}.`);
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible guardar la ficha."); }
    finally { setSaving(false); }
  }

  async function updatePrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    const isInitialCapture = editing.salePrice === null;
    const parsed = lensPriceComponentUpdateSchema.safeParse({
      acquisitionCost: editCost.trim() ? Number(editCost) : null,
      salePrice: editPrice.trim() ? Number(editPrice) : null,
      status: isInitialCapture ? "ACTIVE" : editStatus,
      reason: isInitialCapture ? "Captura inicial de precios en ficha previamente cargada" : editReason,
    });
    if (!parsed.success || (isInitialCapture && parsed.data.salePrice === null)) {
      setMessage(isInitialCapture
        ? "Captura un precio de venta válido para habilitar la ficha en Venta oftálmica."
        : "Captura importes válidos y un motivo de al menos cinco caracteres.");
      return;
    }
    setSaving(true); setMessage("");
    try {
      const result = await persistExistingPrice(editing, parsed.data);
      const verified = result.verified;
      setEditing(verified);
      setEditCost(verified.acquisitionCost?.toString() || "");
      setEditPrice(verified.salePrice?.toString() || "");
      setEditStatus(verified.status);
      setEditReason("");
      setMessage(verified.readyForSale
        ? `${isInitialCapture ? "Captura inicial" : "Cambio"} guardado y verificado: ${verified.label} · ${money(verified.salePrice)}. ${result.affectedCount} combinaciones recalculadas; ${result.readyCount} listas para Venta oftálmica.`
        : `Guardado y verificado: ${verified.label}. Falta su precio de venta; ${result.affectedCount} combinaciones siguen bloqueadas.`);
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible actualizar la ficha."); }
    finally { setSaving(false); }
  }

  async function saveCombination(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const alternateSuppliers = combination.alternateSuppliers
      .split(",").map((value) => value.trim()).filter(Boolean);
    const payload = {
      basePriceId: combination.basePriceId,
      arPriceId: combination.arPriceId || null,
      photoPriceId: combination.photoPriceId || null,
      photoColor: combination.photoPriceId ? combination.photoColor || null : null,
      uvPriceId: combination.uvPriceId || null,
      appliesRangeRx: combination.appliesRangeRx,
      rangePriceId: combination.appliesRangeRx ? combination.rangePriceId || null : null,
      preferredSupplier: combination.preferredSupplier.trim() || null,
      alternateSuppliers,
    };
    const parsed = lensPriceCombinationInputSchema.safeParse(payload);
    if (!parsed.success) { setMessage("Revisa la base, los tratamientos, el color y el rango RX."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-combinations`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = lensPriceCombinationResponseSchema.parse(await response.json()).data;
      await loadLensCatalog(); setCombination(blankLensCombination());
      setMessage(saved.readyForSale
        ? `Combinación autorizada y protegida. Precio total: ${money(saved.totalPrice)}.`
        : "Combinación protegida. Permanecerá fuera de ventas hasta completar todos sus precios.");
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible guardar la combinación."); }
    finally { setSaving(false); }
  }

  async function importProtectedTemplate() {
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-template-import`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: "IMPORTAR_CATALOGO_43_227_SIN_PRECIOS" }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = await response.json() as { data?: {
        importedPrices?: number; importedCombinations?: number;
        pendingPrices?: number; readyForSale?: number;
      } };
      await loadLensCatalog();
      setMessage(`Carga verificada: ${result.data?.importedPrices || 0} fichas y ${result.data?.importedCombinations || 0} combinaciones. ${result.data?.pendingPrices || 0} precios pendientes; ${result.data?.readyForSale || 0} combinaciones disponibles para venta.`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible cargar el catálogo protegido.");
    } finally { setSaving(false); }
  }

  async function synchronizeDerivedCombinations() {
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-combinations/synchronize-derived`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: "SINCRONIZAR_PARIDAD_COMBINACIONES_1024" }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = await response.json() as { data?: {
        storedCombinations?: number; arPhotoCombinations?: number;
        rangeCombinations?: number; derivedCombinations?: number;
        availableCombinations?: number;
      } };
      await loadLensCatalog();
      setMessage(`Paridad aplicada: ${result.data?.storedCombinations || 0} combinaciones registradas + ${result.data?.derivedCombinations || 0} derivadas = ${result.data?.availableCombinations || 0} disponibles. Incluye ${result.data?.arPhotoCombinations || 0} cruces de antirreflejante/fotosensible y ${result.data?.rangeCombinations || 0} reglas de rango RX.`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible sincronizar las reglas derivadas.");
    } finally { setSaving(false); }
  }

  async function applySafeLensRoutes() {
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/lens-routes/apply-safe-extension`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: "AMPLIAR_CATALOGO_LENTES_49_252" }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = await response.json() as { data?: {
        activePrices?: number; storedCombinations?: number; derivedCombinations?: number;
        availableCombinations?: number; pendingPrices?: number; blockedRoutes?: string[];
      } };
      await loadLensCatalog();
      setMessage(`Ampliación segura aplicada: ${result.data?.activePrices || 0} fichas, ${result.data?.storedCombinations || 0} combinaciones administradas y ${result.data?.availableCombinations || 0} combinaciones operativas. Los importes nuevos permanecen pendientes. Rutas contradictorias bloqueadas: ${(result.data?.blockedRoutes || []).join("; ")}.`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible aplicar la ampliación segura.");
    } finally { setSaving(false); }
  }

  if (loading || !options || !draft) return <section className="loading-panel"><span className="loading-ring" aria-hidden="true" /><h1>Cargando precios de lentes</h1><p>Preparando fichas y combinaciones permitidas…</p></section>;

  const activePrices = prices.filter((price) => price.status === "ACTIVE");
  const activeCombinations = combinations.filter((item) => item.status === "ACTIVE");
  const byType = (type: LensPriceComponentType) => activePrices.filter((price) => price.componentType === type);
  const optionValues = draft.componentType === "AR"
    ? options.antireflective
    : draft.componentType === "FOTOSENSIBLE"
      ? options.photosensitive
      : options.uvColor;

  return (
    <section className="admin-shell lens-catalog-shell" aria-labelledby="lens-catalog-title">
      <div className="section-heading"><div><p className="eyebrow">Catálogo central</p><h1 id="lens-catalog-title">Precios de lentes</h1><p>Las mismas fichas y reglas comerciales de la plataforma vigente, sin manejar existencias físicas de lentes.</p></div><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div>
      <nav className="catalog-tabs" aria-label="Secciones del catálogo"><button className="secondary-button" onClick={onProducts}>Productos y armazones</button><button className="primary-button" aria-current="page">Precios de lentes</button><button className="secondary-button" onClick={onInventory}>Inventario y remesas</button></nav>
      {canImportProtectedTemplate && <div className="form-actions"><button className="secondary-button" disabled={saving} onClick={importProtectedTemplate} type="button">Cargar catálogo protegido 43/227</button><button className="secondary-button" disabled={saving} onClick={synchronizeDerivedCombinations} type="button">Recalcular paridad de reglas</button><button className="primary-button" disabled={saving} onClick={applySafeLensRoutes} type="button">Agregar 6 rutas seguras</button></div>}
      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

      <div className="lens-card-grid">
        {lensCardDetails.map((card) => <button className={draft.componentType === card.type ? "lens-type-card selected" : "lens-type-card"} key={card.type} onClick={() => selectCard(card.type)} type="button"><span>{activePrices.filter((price) => price.componentType === card.type).length} fichas</span><strong>{card.title}</strong><small>{card.description}</small></button>)}
      </div>

      {canManage && (editing ? <form className="user-editor lens-price-editor" onSubmit={updatePrice}>
        <div className="editor-title"><div><h2>{editing.salePrice === null ? "Captura inicial de precios" : "Modificar ficha"}</h2><p>{editing.priceCode} · {editing.label}</p></div><button className="text-button" onClick={() => { setEditing(null); setMessage(""); }} type="button">Cancelar</button></div>
        <div className="field-grid"><label>Costo de adquisición con IVA<input disabled={saving} min="0.01" onChange={(event) => setEditCost(event.target.value)} placeholder="Pendiente" step="0.01" type="number" value={editCost} /></label><label>Precio de venta con IVA<input disabled={saving} min="0.01" onChange={(event) => setEditPrice(event.target.value)} placeholder="Necesario para Venta oftálmica" required={editing.salePrice === null} step="0.01" type="number" value={editPrice} /></label>{editing.salePrice !== null && <><label>Estado<select disabled={saving} onChange={(event) => setEditStatus(event.target.value as "ACTIVE" | "INACTIVE")} value={editStatus}><option value="ACTIVE">Activa</option><option value="INACTIVE">Inactiva</option></select></label><label>Motivo del cambio<input disabled={saving} minLength={5} onChange={(event) => setEditReason(event.target.value)} placeholder="Obligatorio para auditoría" required value={editReason} /></label></>}</div>
        <button className="primary-button" disabled={saving}>{saving ? "Guardando…" : editing.salePrice === null ? "Guardar precios y habilitar en Venta oftálmica" : "Guardar cambio con historial"}</button>
      </form> : <form className="user-editor lens-price-editor" onSubmit={savePrice}>
        <div className="editor-title"><div><h2>Nueva ficha · {lensCardDetails.find((card) => card.type === draft.componentType)?.title}</h2><p>La identidad quedará protegida; costo y precio podrán actualizarse con motivo.</p></div></div>
        {draft.componentType === "BASE" ? <div className="field-grid"><label>Tipo de lente<select disabled={saving} onChange={(event) => { const lensType = event.target.value as LensType; setDraft({ ...draft, lensType, design: options.designsByType[lensType][0] || "Convencional" }); }} value={draft.lensType}>{options.lensTypes.map((value) => <option key={value}>{value}</option>)}</select></label><label>Diseño<select disabled={saving} onChange={(event) => setDraft({ ...draft, design: event.target.value as LensDesign })} value={draft.design}>{options.designsByType[draft.lensType].map((value) => <option key={value}>{value}</option>)}</select></label><label>Material<select disabled={saving} onChange={(event) => setDraft({ ...draft, material: event.target.value as LensMaterial })} value={draft.material}>{options.materials.map((value) => <option key={value}>{value}</option>)}</select></label></div> : draft.componentType !== "RANGO_RX" && <div className="field-grid"><label>Concepto<select disabled={saving} onChange={(event) => setDraft({ ...draft, optionName: event.target.value })} value={draft.optionName}>{optionValues.map((value) => <option key={value}>{value}</option>)}</select></label></div>}
        <div className="field-grid"><label>Costo de adquisición con IVA<input disabled={saving} min="0.01" onChange={(event) => setDraft({ ...draft, acquisitionCost: event.target.value })} placeholder="Puede capturarse después" step="0.01" type="number" value={draft.acquisitionCost} /></label><label>Precio de venta con IVA<input disabled={saving} min="0.01" onChange={(event) => setDraft({ ...draft, salePrice: event.target.value })} placeholder="Puede capturarse después" step="0.01" type="number" value={draft.salePrice} /></label></div>
        <button className="primary-button" disabled={saving}>{saving ? "Guardando…" : "Guardar ficha de precio"}</button>
      </form>)}

      <section className="user-directory lens-price-directory"><div className="directory-toolbar"><div><h2>Fichas vigentes</h2><p>{activePrices.length} ficha{activePrices.length === 1 ? "" : "s"} activas. Las pendientes no se ofrecen en venta.</p></div></div><div className="lens-price-list">{activePrices.map((price) => <article className="lens-price-row" key={price.id}><div><span className="record-code">{price.priceCode}</span><h3>{price.label}</h3><p>{lensCardDetails.find((card) => card.type === price.componentType)?.title}</p></div><div className="lens-price-amount"><span>Venta {money(price.salePrice)}</span><small>Costo {money(price.acquisitionCost)}</small><span className={price.readyForSale ? "active-status" : "inactive-status"}>{price.readyForSale ? "Lista para venta" : "Precio pendiente"}</span>{canManage && <button className="text-button" onClick={() => startEditing(price)}>{price.readyForSale ? "Modificar ficha" : "Capturar precios"}</button>}</div></article>)}{activePrices.length === 0 && <p className="empty-state">Todavía no hay fichas de precio activas.</p>}</div></section>

      <section className="user-directory lens-combination-section"><div className="directory-toolbar"><div><h2>Combinaciones permitidas</h2><p>{activeCombinations.length} combinaciones protegidas; {activeCombinations.filter((item) => item.readyForSale).length} listas para venta.</p></div></div>
        {canManage && <form className="combination-form" onSubmit={saveCombination}><div className="field-grid"><label>Precio base<select disabled={saving} onChange={(event) => setCombination({ ...combination, basePriceId: event.target.value })} required value={combination.basePriceId}><option value="">Selecciona</option>{byType("BASE").map((price) => <option key={price.id} value={price.id}>{price.label} · {money(price.salePrice)}</option>)}</select></label><label>Antirreflejante<select disabled={saving} onChange={(event) => setCombination({ ...combination, arPriceId: event.target.value })} value={combination.arPriceId}><option value="">Sin antirreflejante</option>{byType("AR").map((price) => <option key={price.id} value={price.id}>{price.label}</option>)}</select></label><label>Fotosensible<select disabled={saving} onChange={(event) => setCombination({ ...combination, photoPriceId: event.target.value, photoColor: "" })} value={combination.photoPriceId}><option value="">Sin fotosensible</option>{byType("FOTOSENSIBLE").map((price) => <option key={price.id} value={price.id}>{price.label}</option>)}</select></label>{combination.photoPriceId && <label>Color fotosensible<select disabled={saving} onChange={(event) => setCombination({ ...combination, photoColor: event.target.value })} required value={combination.photoColor}><option value="">Selecciona</option>{options.photoColors.map((value) => <option key={value}>{value}</option>)}</select></label>}<label>Color o espejeado<select disabled={saving} onChange={(event) => setCombination({ ...combination, uvPriceId: event.target.value })} value={combination.uvPriceId}><option value="">Sin color o espejeado</option>{byType("UV_COLOR").map((price) => <option key={price.id} value={price.id}>{price.label}</option>)}</select></label><label className="checkbox-label"><input checked={combination.appliesRangeRx} disabled={saving} onChange={(event) => setCombination({ ...combination, appliesRangeRx: event.target.checked, rangePriceId: "" })} type="checkbox" />Aplica cargo por rango RX</label>{combination.appliesRangeRx && <label>Precio de rango RX<select disabled={saving} onChange={(event) => setCombination({ ...combination, rangePriceId: event.target.value })} required value={combination.rangePriceId}><option value="">Selecciona</option>{byType("RANGO_RX").map((price) => <option key={price.id} value={price.id}>{price.label} · {money(price.salePrice)}</option>)}</select></label>}<label>Proveedor preferente<input disabled={saving} onChange={(event) => setCombination({ ...combination, preferredSupplier: event.target.value })} value={combination.preferredSupplier} /></label><label>Proveedores alternos<input disabled={saving} onChange={(event) => setCombination({ ...combination, alternateSuppliers: event.target.value })} placeholder="Hasta 5, separados por coma" value={combination.alternateSuppliers} /></label></div><button className="primary-button" disabled={saving || byType("BASE").length === 0}>{saving ? "Guardando…" : "Autorizar combinación"}</button></form>}
        <div className="combination-list">{activeCombinations.map((item) => <article className="combination-row" key={item.id}><div><span className="record-code">{item.combinationCode}</span><h3>{item.baseLabel}</h3><p>{[item.arLabel, item.photoLabel && `${item.photoLabel} ${item.photoColor}`, item.uvLabel, item.appliesRangeRx && "Rango RX"].filter(Boolean).join(" · ") || "Sin tratamientos adicionales"}</p><small>{item.preferredSupplier ? `Proveedor preferente: ${item.preferredSupplier}` : "Sin proveedor preferente"}</small><small className="combination-formula">{item.priceBreakdown.map((component) => `${component.label}: ${money(component.salePrice)}`).join(" + ")}</small>{item.missingPriceComponents.length > 0 && <small className="combination-missing">Falta: {item.missingPriceComponents.join("; ")}</small>}</div><div className="combination-total"><strong>{item.readyForSale ? money(item.totalPrice) : "Precio pendiente"}</strong><small>{item.readyForSale ? "Suma automática verificada" : "No disponible para venta"}</small></div></article>)}{activeCombinations.length === 0 && <p className="empty-state">Todavía no hay combinaciones autorizadas.</p>}</div>
      </section>
    </section>
  );
}

function CatalogModule({ session, onClose }: { session: SessionIdentity; onClose: () => void }) {
  const [catalogView, setCatalogView] = useState<"products" | "lens-prices" | "inventory">("products");
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [form, setForm] = useState<CatalogForm | null>(null);
  const [resolvedProduct, setResolvedProduct] = useState<CatalogProduct | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const canManage = session.roles.includes("ADMINISTRADOR") && session.permissions.includes("INVENTORY_WRITE") && session.permissions.includes("ADMIN_WRITE");

  async function loadProducts(term = "") {
    const response = await fetch(`${apiBaseUrl}/api/v1/catalog/products?search=${encodeURIComponent(term)}`, { credentials: "include", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(await responseMessage(response));
    setProducts(catalogProductsResponseSchema.parse(await response.json()).data);
  }

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${apiBaseUrl}/api/v1/catalog/categories`, { credentials: "include", headers: { Accept: "application/json" } }),
      fetch(`${apiBaseUrl}/api/v1/catalog/products`, { credentials: "include", headers: { Accept: "application/json" } }),
    ])
      .then(async ([categoriesResponse, productsResponse]) => {
        if (!categoriesResponse.ok) throw new Error(await responseMessage(categoriesResponse));
        if (!productsResponse.ok) throw new Error(await responseMessage(productsResponse));
        const nextCategories = inventoryCategoriesResponseSchema.parse(await categoriesResponse.json()).data;
        const nextProducts = catalogProductsResponseSchema.parse(await productsResponse.json()).data;
        if (!active) return;
        setCategories(nextCategories); setProducts(nextProducts); setForm(blankCatalogForm(nextCategories));
      })
      .catch((cause: unknown) => { if (active) setMessage(cause instanceof Error ? cause.message : "No fue posible cargar el catálogo."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  function startNew() {
    setResolvedProduct(null); setForm(blankCatalogForm(categories)); setMessage("");
  }

  async function resolvePartNumber() {
    if (!form?.partNumber.trim()) { setMessage("Captura el número de parte para buscarlo en el catálogo central."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/catalog/resolve-part?partNumber=${encodeURIComponent(form.partNumber)}`, { credentials: "include", headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(await responseMessage(response));
      const product = catalogProductResolutionResponseSchema.parse(await response.json()).data;
      if (product) {
        setResolvedProduct(product); setForm(catalogFormFromProduct(product));
        setMessage(`Número de parte existente: se reutilizará el SKU ${product.sku}. La identidad y medidas quedaron protegidas.`);
      } else {
        setResolvedProduct(null); setMessage("Número de parte disponible. El SKU universal se asignará al guardar.");
      }
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible consultar el número de parte."); }
    finally { setSaving(false); }
  }

  function editPrice(product: CatalogProduct) {
    setResolvedProduct(product); setForm(catalogFormFromProduct(product));
    setMessage(`Actualizarás el precio universal del SKU ${product.sku}; su identidad y medidas no cambiarán.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function saveProduct(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    const pricePayload = {
      acquisitionCost: Number(form.acquisitionCost), multiplier: Number(form.multiplier),
      manualPublicPrice: form.manualPublicPrice ? Number(form.manualPublicPrice) : null,
      status: form.status,
    };
    const createPayload = {
      partNumber: form.partNumber.trim(), categoryCode: form.categoryCode,
      brand: form.brand.trim(), material: form.material.trim(), color: form.color.trim(),
      measureA: form.measureA ? Number(form.measureA) : null,
      measureB: form.measureB ? Number(form.measureB) : null,
      measureDbl: form.measureDbl ? Number(form.measureDbl) : null,
      measureEd: form.measureEd ? Number(form.measureEd) : null,
      baseCurve: form.baseCurve ? Number(form.baseCurve) : null,
      ...pricePayload,
    };
    const parsed = resolvedProduct ? catalogPriceUpdateSchema.safeParse(pricePayload) : catalogProductCreateSchema.safeParse(createPayload);
    if (!parsed.success) { setMessage("Revisa la identidad, las medidas y los datos de precio antes de guardar."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(resolvedProduct ? `${apiBaseUrl}/api/v1/catalog/products/${resolvedProduct.id}/price` : `${apiBaseUrl}/api/v1/catalog/products`, {
        method: resolvedProduct ? "PATCH" : "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(parsed.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = catalogProductResponseSchema.parse(await response.json()).data;
      await loadProducts(search); setResolvedProduct(saved); setForm(catalogFormFromProduct(saved));
      setMessage(resolvedProduct ? `Precio del SKU ${saved.sku} actualizado. Precio público vigente: ${money(saved.publicPrice)}.` : `Producto creado con el SKU universal ${saved.sku}.`);
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible guardar el producto."); }
    finally { setSaving(false); }
  }

  async function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setLoading(true);
    try { await loadProducts(search); }
    catch (cause) { setMessage(cause instanceof Error ? cause.message : "No fue posible consultar el catálogo."); }
    finally { setLoading(false); }
  }

  if (catalogView === "lens-prices") {
    return <LensPriceCatalog session={session} onClose={onClose} onProducts={() => setCatalogView("products")} onInventory={() => setCatalogView("inventory")} />;
  }
  if (catalogView === "inventory") {
    return <InventoryOperations session={session} onClose={onClose} onProducts={() => setCatalogView("products")} onLensPrices={() => setCatalogView("lens-prices")} />;
  }
  if (loading || !form) return <section className="loading-panel"><span className="loading-ring" aria-hidden="true" /><h1>Cargando catálogo</h1><p>Preparando categorías y productos…</p></section>;
  const category = categories.find((item) => item.code === form.categoryCode);
  const calculatedPreview = previewPublicPrice(form, resolvedProduct?.publicPrice || 0);
  const identityLocked = Boolean(resolvedProduct);

  return (
    <section className="admin-shell catalog-shell" aria-labelledby="catalog-title">
      <div className="section-heading"><div><p className="eyebrow">Catálogo central</p><h1 id="catalog-title">Catálogo e inventario</h1><p>Un número de parte corresponde a un solo SKU en toda la empresa.</p></div><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div>
      <nav className="catalog-tabs" aria-label="Secciones del catálogo"><button className="primary-button" aria-current="page">Productos y armazones</button><button className="secondary-button" onClick={() => setCatalogView("lens-prices")}>Precios de lentes</button><button className="secondary-button" onClick={() => setCatalogView("inventory")}>Inventario y remesas</button></nav>
      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}
      <div className={canManage ? "catalog-grid" : "catalog-grid catalog-grid-readonly"}>
        {canManage && <form className="user-editor catalog-editor" onSubmit={saveProduct}>
          <div className="editor-title"><div><h2>{identityLocked ? `SKU ${resolvedProduct?.sku}` : "Nuevo producto"}</h2><p>{identityLocked ? "Actualiza costo y precio sin duplicar el producto." : "Primero valida el número de parte."}</p></div>{identityLocked && <button className="text-button" onClick={startNew} type="button">Nuevo producto</button>}</div>
          <div className="part-lookup"><label>Número de parte<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, partNumber: event.target.value })} required value={form.partNumber} /></label><button className="secondary-button compact-button" disabled={saving || identityLocked} onClick={resolvePartNumber} type="button">Validar número</button></div>
          <div className="field-grid">
            <label>Categoría<select disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, categoryCode: event.target.value as InventoryCategory["code"], measureA: "", measureB: "", measureDbl: "", measureEd: "", baseCurve: "" })} value={form.categoryCode}>{categories.map((item) => <option key={item.code} value={item.code}>{item.labelCode} · {item.name}</option>)}</select></label>
            <label>Marca<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, brand: event.target.value })} required value={form.brand} /></label>
            <label>Material<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, material: event.target.value })} required value={form.material} /></label>
            <label>Color<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, color: event.target.value })} required value={form.color} /></label>
          </div>
          {category?.gradable && <fieldset><legend>Medidas del armazón graduable</legend><div className="measurement-grid"><label>A · 38–71 mm<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, measureA: event.target.value })} required type="number" value={form.measureA} /></label><label>B · 22–51 mm<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, measureB: event.target.value })} required type="number" value={form.measureB} /></label><label>DBL · 10–21 mm<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, measureDbl: event.target.value })} required type="number" value={form.measureDbl} /></label><label>ED · 39–71 mm<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, measureEd: event.target.value })} required type="number" value={form.measureEd} /></label>{form.categoryCode === "Sol G" && <label>Curva base · 1–6<input disabled={saving || identityLocked} onChange={(event) => setForm({ ...form, baseCurve: event.target.value })} required type="number" value={form.baseCurve} /></label>}</div></fieldset>}
          <fieldset><legend>Precio universal</legend><div className="field-grid"><label>Costo de adquisición con IVA<input disabled={saving} min="0.01" onChange={(event) => setForm({ ...form, acquisitionCost: event.target.value })} required step="0.01" type="number" value={form.acquisitionCost} /></label><label>Multiplicador<input disabled={saving} min="0.01" onChange={(event) => setForm({ ...form, multiplier: event.target.value })} required step="0.01" type="number" value={form.multiplier} /></label><label>Precio público manual<input disabled={saving} min="0.01" onChange={(event) => setForm({ ...form, manualPublicPrice: event.target.value })} step="0.01" type="number" value={form.manualPublicPrice} /></label>{identityLocked && <label>Estado<select disabled={saving} onChange={(event) => setForm({ ...form, status: event.target.value as "ACTIVE" | "INACTIVE" })} value={form.status}><option value="ACTIVE">Activo</option><option value="INACTIVE">Inactivo</option></select></label>}</div><div className="price-preview"><span>Precio público estimado</span><strong>{calculatedPreview > 0 ? money(calculatedPreview) : "Captura costo y multiplicador"}</strong><small>Se conserva el precio anterior cuando es mayor.</small></div></fieldset>
          <button className="primary-button" disabled={saving}>{saving ? "Guardando…" : identityLocked ? "Actualizar precio universal" : "Crear producto y asignar SKU"}</button>
        </form>}
        <div className="user-directory catalog-directory">
          <form className="directory-toolbar" onSubmit={submitSearch}><div><h2>Productos registrados</h2><p>{products.length} producto{products.length === 1 ? "" : "s"} en esta consulta.</p></div><div className="search-actions"><input aria-label="Buscar producto" onChange={(event) => setSearch(event.target.value)} placeholder="SKU, parte o marca" type="search" value={search} /><button className="secondary-button compact-button">Buscar</button></div></form>
          <div className="product-list">{products.map((product) => <article className="product-row" key={product.id}><div className="product-sku"><span>SKU</span><strong>{product.sku}</strong></div><div className="product-identity"><h3>{product.brand} · {product.color}</h3><p>{product.categoryLabel} · {product.material} · Parte {product.partNumber}</p><div className="tag-list">{product.gradable && <span>A {product.measureA} · B {product.measureB} · DBL {product.measureDbl} · ED {product.measureEd}</span>}{product.baseCurve && <span>Curva {product.baseCurve}</span>}<span className={product.status === "ACTIVE" ? "active-status" : "inactive-status"}>{product.status === "ACTIVE" ? "Activo" : "Inactivo"}</span></div></div><div className="product-price"><span>Precio público</span><strong>{money(product.publicPrice)}</strong><small>Costo {money(product.acquisitionCost)}</small>{canManage && <button className="text-button" onClick={() => editPrice(product)}>Actualizar precio</button>}</div></article>)}{products.length === 0 && <p className="empty-state">No hay productos registrados con ese criterio.</p>}</div>
        </div>
      </div>
    </section>
  );
}

function Workspace({ session, onLogout, onSessionUpdated }: { session: SessionIdentity; onLogout: () => void; onSessionUpdated: (session: SessionIdentity) => void }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const [view, setView] = useState<"home" | "users" | "cash-drawer" | "catalog" | "sales" | "counter-sales" | "work-control" | "clinical" | "billing" | "kpis" | "cancellations">("home");
  const [cancellationSaleFolio, setCancellationSaleFolio] = useState("");
  const [switchingBranch, setSwitchingBranch] = useState(false);
  const canManageUsers = session.roles.includes("ADMINISTRADOR") && session.permissions.includes("ADMIN_WRITE");
  const canReadCatalog = session.permissions.includes("INVENTORY_READ");
  const canReadSales = session.permissions.includes("SALES_READ");
  const canWriteSales = session.permissions.includes("SALES_WRITE");
  const canReadLaboratory = session.permissions.includes("LAB_READ");
  const canReadClinical = session.permissions.includes("CLINICAL_READ");
  const canReadBilling = session.permissions.includes("BILLING_READ");
  const canReadKpis = session.permissions.includes("KPI_READ");
  const canReadCancellations = getCancellationAccess(session).canRead;
  const currentBranch = session.branches.find((branch) => branch.id === session.branchId);

  useEffect(() => {
    const openCancellations = () => { if (canReadCancellations) { setCancellationSaleFolio(""); setView("cancellations"); } };
    window.addEventListener("cosmo:open-cancellations", openCancellations);
    return () => window.removeEventListener("cosmo:open-cancellations", openCancellations);
  }, [canReadCancellations]);

  async function logout() {
    setLoggingOut(true);
    try { await fetch(`${apiBaseUrl}/api/v1/auth/logout`, { method: "POST", credentials: "include", headers: { Accept: "application/json" } }); }
    finally { onLogout(); setLoggingOut(false); }
  }

  async function changeBranch(branchId: string) {
    if (!branchId || branchId === session.branchId) return;
    setSwitchingBranch(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/select-branch`, { method: "POST", credentials: "include", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify({ branchId }) });
      if (!response.ok) throw new Error(await responseMessage(response));
      onSessionUpdated(sessionResponseSchema.parse(await response.json()).data);
    } finally { setSwitchingBranch(false); }
  }

  if (view === "users") return <UserAdministration session={session} onCashDrawer={() => setView("cash-drawer")} onClose={() => setView("home")} />;
  if (view === "cash-drawer") return <CashDrawerAdministration session={session} onBack={() => setView("users")} onClose={() => setView("home")} />;
  if (view === "catalog") return <CatalogModule session={session} onClose={() => setView("home")} />;
  if (view === "sales") return <SalesOperations session={session} onClose={() => setView("home")} />;
  if (view === "counter-sales") return <CounterSalesOperations session={session} onClose={() => setView("home")} />;
  if (view === "work-control") return <WorkControlOperations session={session} onClose={() => setView("home")} onOpenCancellation={(folio) => {
    if (canReadCancellations) { setCancellationSaleFolio(folio); setView("cancellations"); }
  }} />;
  if (view === "clinical") return <ClinicalRecordsOperations session={session} onClose={() => setView("home")} />;
  if (view === "billing") return <BillingOperations session={session} onClose={() => setView("home")} />;
  if (view === "kpis") return <KpiOperations session={session} onClose={() => setView("home")} />;
  if (view === "cancellations") return <CancellationOperations session={session} initialSaleFolio={cancellationSaleFolio} onClose={() => { setCancellationSaleFolio(""); setView("home"); }} />;

  return (
    <>
      <section className="hero workspace-hero" aria-labelledby="page-title"><div><p className="eyebrow">Sesión protegida</p><h1 id="page-title">Bienvenido, {session.displayName}.</h1><p className="hero-copy">Tu acceso está separado por empresa, usuario, permisos y sucursal.</p></div><div className="session-actions">{session.branches.length > 1 ? <label className="branch-switcher">Sucursal<select aria-label="Sucursal activa" disabled={switchingBranch} onChange={(event) => changeBranch(event.target.value)} value={session.branchId || ""}>{session.branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.code} · {branch.name}</option>)}</select></label> : currentBranch && <div className="branch-chip"><span>Sucursal activa</span><strong>{currentBranch.name}</strong></div>}<button className="secondary-button" disabled={loggingOut} onClick={logout}>{loggingOut ? "Cerrando…" : "Cerrar sesión"}</button></div></section>
      <section className="foundation-grid" aria-label="Módulos de la plataforma">
        <ModuleCard action="Abrir venta graduada" description="Cliente, armazón, receta, configuración guiada de lentes y cotización." enabled={canReadSales} icon="graduated" onOpen={() => setView("sales")} title="Venta graduada" />
        <ModuleCard action="Abrir venta de mostrador" description="Productos por SKU, carrito, cobro y apartado con inventario protegido." enabled={canWriteSales} icon="counter" onOpen={() => setView("counter-sales")} title="Venta de mostrador" />
        <ModuleCard action="Abrir catálogo" description="SKU universal, identidad, medidas y precios protegidos." enabled={canReadCatalog} icon="inventory" onOpen={() => setView("catalog")} title="Catálogo e inventario" />
        <ModuleCard action="Abrir control de trabajos" description="Envíos a laboratorio, recepción, cobros, avisos por WhatsApp, entrega y garantías." enabled={canReadLaboratory} icon="work" onOpen={() => setView("work-control")} title="Control de trabajos" />
        <ModuleCard action="Abrir expedientes" description="Historias clínicas, tickets de equipos, pendientes y cierre mensual protegido." enabled={canReadClinical} icon="clinical" onOpen={() => setView("clinical")} title="Expedientes clínicos" />
        <ModuleCard action="Abrir facturación" description="Solicitudes fiscales, XML, PDF, representación de 80 mm y entrega auditada." enabled={canReadBilling} icon="billing" onOpen={() => setView("billing")} title="Facturación" />
        <ModuleCard action="Abrir KPIs" description="Tráfico, ventas, conversiones, productos y avance contra objetivos." enabled={canReadKpis} icon="kpi" onOpen={() => setView("kpis")} title="KPIs de sucursal" />
        <ModuleCard action="Administrar usuarios" description={canManageUsers ? "Usuarios, perfiles, sucursales y auditoría disponibles." : "Acceso según los permisos asignados."} enabled={canManageUsers} icon="admin" onOpen={() => setView("users")} title="Administración" />
        <ModuleCard action="Abrir cancelaciones" description="Solicitudes, revisión independiente, claves de un solo uso y notas de crédito." enabled={canReadCancellations} icon="cancellation" onOpen={() => { setCancellationSaleFolio(""); setView("cancellations"); }} title="Cancelaciones" />
      </section>
      <aside className="notice"><strong>Entorno de validación:</strong> no contiene información real de clientes, ventas o expedientes.</aside>
    </>
  );
}

export function App() {
  const [apiState, setApiState] = useState<ApiState>({ kind: "loading" });
  const [authState, setAuthState] = useState<AuthState>({ kind: "checking" });

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([loadHealth(controller.signal), loadSession(controller.signal)])
      .then(([health, session]) => { setApiState({ kind: "ready", health }); setAuthState(session ? { kind: "authenticated", session } : { kind: "anonymous" }); })
      .catch((error: unknown) => { if (error instanceof DOMException && error.name === "AbortError") return; setApiState({ kind: "offline" }); setAuthState({ kind: "anonymous" }); });
    return () => controller.abort();
  }, []);

  const session = authState.kind === "authenticated" ? authState.session : undefined;
  const logoutLocally = () => setAuthState({ kind: "anonymous" });
  const updateSession = (nextSession: SessionIdentity) => setAuthState({ kind: "authenticated", session: nextSession });

  return (
    <main className="page-shell">
      <BrandHeader session={session} />
      {authState.kind === "checking" && <section className="loading-panel" aria-live="polite"><span className="loading-ring" aria-hidden="true" /><h1>Cargando acceso</h1><p>Validando la disponibilidad de tu espacio de trabajo…</p></section>}
      {authState.kind === "anonymous" && <LoginPanel enabled={apiState.kind === "ready"} onAuthenticated={updateSession} />}
      {authState.kind === "authenticated" && session?.mustChangePassword && <PasswordChangePanel session={session} onLogout={logoutLocally} onCompleted={updateSession} />}
      {authState.kind === "authenticated" && session && !session.mustChangePassword && !session.branchId && <BranchSelectionPanel session={session} onLogout={logoutLocally} onSelected={updateSession} />}
      {authState.kind === "authenticated" && session && !session.mustChangePassword && session.branchId && <Workspace session={session} onLogout={logoutLocally} onSessionUpdated={updateSession} />}
    </main>
  );
}
